import { Tooltip } from "@mui/material";
import { unix_utc_to_local_12_hour_clock } from "../../../utils/date_time_utils";
import { t } from "i18next";
import TrackerStatusPill from "../components/TrackerStatusPill";
import TrackerTypePill from "../components/TrackerTypePill";
import TrackerIdentifierPill from "../components/TrackerIdentifierPill";
import LastActivityCell from "../components/LastActivityCell";
import BatteryLifePill from "../components/BatteryLifePill";
import AssociatedItemPill from "../components/AssociatedItemPill";
import ManufacturerPill from "../components/ManufacturerPill";

export async function searchDeviceAssets(
  props,
  joined_device_asset_ids = "",
  limit = 300
) {
  const { apiUrl, token, organizationId } = props;
  const elasticQuery = {
    elasticSearchQuery: {
      bool: {
        must: [
          {
            term: {
              current_owner_id: organizationId,
            },
          },
          {
            terms: {
              parent_id: joined_device_asset_ids,
            },
          },
        ],
      },
    },
    sort: [
      {
        time_of_log: "asc",
      },
      {
        tag: "desc",
      },
    ],
    limit: limit,
  };

  const results = await fetch(`${apiUrl}assets/search?includeConsoleExtras=true`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "auth-token": token,
    },
    body: JSON.stringify(elasticQuery),
  })
    .then((response) => response.json())
    .then((json) => {
      return json;
    })
    .catch((err) => {
      console.log(err);
      console.log(elasticQuery);
      return {
        error: "Failed to fetch data, please contact system administrator.",
      };
    });

  return results;
}

export async function buildDeviceAssetsMap(props, deviceList) {
  const deviceAssetIds =
    deviceList && deviceList.length > 0
      ? deviceList
        .map((d) => {
          if (d.assetId) {
            return d.assetId;
          }
          return null;
        })
        .filter((el) => el !== null)
      : [];

  if (deviceAssetIds.length > 0) {
    const deviceAssetsMap = await searchDeviceAssets(
      props,
      deviceAssetIds
    ).then((res) => {
      if (res.error || !res.assets) {
        return {};
      }
      let deviceAssetsMap = {};
      res.assets.forEach((ass) => {
        if (ass.parentId) {
          if (!deviceAssetsMap[ass.parentId]) {
            deviceAssetsMap[ass.parentId] = [];
          }
          deviceAssetsMap[ass.parentId].push(ass);
        }
      });

      return deviceAssetsMap;
    });
    return deviceAssetsMap;
  }

  return {};
}

export const unassignDevices = async (
  props,
  selectedDevices = {},
  device_asset_map = {}
) => {
  const { apiUrl, token } = props;
  const deviceAssets = Object.keys(device_asset_map)
    .filter((d) => selectedDevices[d])
    .map((d) => {
      return {
        deviceId: d,
        associatedAssets: device_asset_map[d],
      };
    });

  const iterateAndFetch = deviceAssets.map(async (d) => {
    const unassignAssets = d.associatedAssets.map(async (ass) => {
      const unassign = await fetch(`${apiUrl}assets/${ass.assetId}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "auth-token": token,
        },
        body: JSON.stringify({ parentId: null }),
      })
        .then((res) => res.json())
        .then((res) => {
          return res;
        })
        .catch((err) => {
          return {
            error: err,
          };
        });
      return unassign;
    });

    const results = await Promise.all(unassignAssets).then(
      async (completed) => {
        // if any failed, then return error message from backend
        if (completed.some((res) => res.error)) {
          return completed.find((res) => res.error);
        }
        const setStatus = await fetch(`${apiUrl}assets/${d.deviceId}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            "auth-token": token,
          },
          body: JSON.stringify({ device: { status: "Unassigned" } }),
        })
          .then((res) => res.json())
          .then((res) => {
            return res;
          })
          .catch((err) => {
            return {
              error: err,
            };
          });
        return setStatus;
      }
    );
    return results;
  });

  return await Promise.all(iterateAndFetch).then((completed) => {
    // if any failures, return error message from backend
    if (completed.some((res) => res.error)) {
      return completed.find((res) => res.error);
    }
    return completed;
  });
};

export const dissociateDevices = async (props, selectedDevicesIds) => {
  const { apiUrl, token } = props;
  const iterateAndFetch = selectedDevicesIds.map(async (currentDeviceId) => {
    const results = await fetch(`${apiUrl}assets/${currentDeviceId}/action`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "auth-token": token,
      },
      body: JSON.stringify({
        action: "dissociate",
        propertiesMap: {
          formData: [],
        },
      }),
    })
      .then((res) => res.json())
      .then((res) => {
        return res;
      })
      .catch((err) => {
        return {
          error: err,
        };
      });

    return results;
  });

  return await Promise.all(iterateAndFetch).then((completed) => {
    // if any failures, return error message from backend
    if (completed.some((res) => res.error)) {
      return completed.find((res) => res.error);
    }
    return completed;
  });
};

export const trackerTableColumns = (props) => {
  const { timeZone = '', history = [], deviceAssetsMap = {} } = props || {};
  return [
    {
      field: "timeCreatedLong",
      align: "center",
      groupable: false,
      headerName: "Created Date",
      description: "When the tracker record was created",
      headerAlign: "center",
      id: "timeCreatedLong",
      width: 155,
      minWidth: 155,
      renderCell: ({ row }) => {
        const { id, timeCreatedLong } = row;
        return (
          <div
            key={id}
          >
            {timeCreatedLong
              ? unix_utc_to_local_12_hour_clock(timeCreatedLong, timeZone)
              : t("dnpinput1")}
          </div>
        );
      },
      type: "dateTime",
      valueGetter: (value, row) => {
        const timeCreatedLong = row.timeCreatedLong;
        return timeCreatedLong
          ? new Date(timeCreatedLong)
          : //furthest date in the future. We want records without a timeCreatedLong to be at the bottom of the table in case of "newest to oldest" sort
          new Date(8640000000000000);
      },
    },
    {
      accessor: "tag",
      field: "tag",
      flex: 2,
      headerName: "Tracker Identifier",
      description: "Tag ID, external ID, or device ID used to identify the tracker",
      headerAlign: "center",
      align: "center",
      id: "tag",
      renderCell: (data) => {
        if (data.rowNode.type === "group" || data.rowNode.type === "pinnedRow") {
          return data.value ?? "";
        }
        const value =
          data.row?.tag ||
          data.row?.externalId ||
          data.row?.device?.deviceId ||
          data.row?.propertiesMap?.nfcId ||
          data.row?.assetId?.substring(0, 8) ||
          "";
        const cell = <TrackerIdentifierPill value={value} />;
        return value ? (
          <Tooltip title={value} enterDelay={400} placement="top-start">
            <span style={{ display: "inline-flex", minWidth: 0, width: "100%", justifyContent: "center" }}>{cell}</span>
          </Tooltip>
        ) : (
          cell
        );
      },
      valueGetter: (value, row) => {
        // For NFC devices, tag may be empty - try fallback identifiers
        return row?.tag ||
          row?.externalId ||
          row?.device?.deviceId ||
          row?.propertiesMap?.nfcId ||
          row?.assetId?.substring(0, 8) || // Show first 8 chars of assetId as last resort
          "";
      },
      groupingValueGetter: (value, row) => {
        return row?.tag ||
          row?.externalId ||
          row?.device?.deviceId ||
          row?.propertiesMap?.nfcId ||
          row?.assetId?.substring(0, 8) ||
          null;
      },
      width: 150,
    },
    {
      field: "timeModifiedLong",
      align: "center",
      groupable: false,
      headerName: "Last Activity",
      description: "Last event time, or last modified/created if no events",
      headerAlign: "center",
      id: "timeModifiedLong",
      width: 200,
      minWidth: 200,
      renderCell: (data) => {
        if (data.rowNode.type === "group" || data.rowNode.type === "pinnedRow") {
          return data.value != null ? String(data.value) : t("dnpinput1");
        }
        const { timeModifiedLong, timeCreatedLong, timeOfLogLong } = data.row;
        const timestamp = timeOfLogLong || timeModifiedLong || timeCreatedLong;
        const displayText = timestamp
          ? unix_utc_to_local_12_hour_clock(timestamp, timeZone)
          : t("dnpinput1");
        const cell = <LastActivityCell displayText={displayText} />;
        return displayText && displayText !== t("dnpinput1") ? (
          <Tooltip title={displayText} enterDelay={400} placement="top-start">
            <span style={{ display: "inline-flex", minWidth: 0, width: "100%", justifyContent: "center" }}>{cell}</span>
          </Tooltip>
        ) : (
          cell
        );
      },
      type: "dateTime",
      valueGetter: (value, row) => {
        // Priority: timeOfLogLong (last event) -> timeModifiedLong -> timeCreatedLong
        const timestamp = row.timeOfLogLong || row.timeModifiedLong || row.timeCreatedLong;
        return timestamp
          ? new Date(timestamp)
          : //furthest date in the future. We want records without a timestamp to be at the bottom of the table in case of "newest to oldest" sort
          new Date(8640000000000000);
      },
    },
    //commented out until descendentsList property is added
    {
      field: "associatedItem",
      align: "center",
      groupable: false,
      headerName: "Associated Item",
      description: "First asset linked to this tracker; click to open asset snapshot",
      headerAlign: "center",
      id: "associatedItem",
      width: 155,
      minWidth: 155,
      renderCell: ({ row }) => {
        const associatedAssets = (deviceAssetsMap && deviceAssetsMap[row?.assetId]) || [];
        const firstAssociated = associatedAssets[0];
        const label = firstAssociated?.tag || "";
        const handleClick = () => {
          if (firstAssociated?.assetId) {
            history.push(`/assetSnapshot/${firstAssociated.assetId}`);
          }
        };
        const pill = <AssociatedItemPill label={label} onClick={handleClick} />;
        return label ? (
          <Tooltip title={label} enterDelay={400} placement="top-start">
            <span style={{ display: "inline-flex", minWidth: 0, width: "100%", justifyContent: "center" }}>{pill}</span>
          </Tooltip>
        ) : (
          pill
        );
      },
      valueGetter: (value, row) => {
        const associatedAssets = (deviceAssetsMap && deviceAssetsMap[row?.assetId]) || [];
        const firstAssociated = associatedAssets[0];
        return firstAssociated?.tag || "";
      },
    },
    {
      accessor: "lastEvent",
      align: "center",
      field: "lastEvent",
      headerName: "Status",
      description: "Whether the tracker is linked to an asset (Associated) or not (Unassociated)",
      headerAlign: "center",
      id: "lastEvent",
      width: 130,
      minWidth: 130,
      renderCell: (data) => {
        if (data.rowNode.type === "group" && data.field === "lastEvent" || data.rowNode.type === "pinnedRow") {
          return null;
        }
        if (data.rowNode.type === "group") {
          return data.value;
        }
        const isAssociated = data.row?.device?.hasDescendents || data.row?.hasDescendents;
        return <TrackerStatusPill isAssociated={!!isAssociated} />;
      },
      valueGetter: (value, row) => {
        const isAssociated = row.device?.hasDescendents || row.hasDescendents;
        return isAssociated ? "Associated" : "Unassociated";
      },
      groupingValueGetter: (value, row) => {
        const isAssociated = row.device?.hasDescendents || row.hasDescendents;
        return isAssociated ? "Associated" : "Unassociated";
      }
    },
    {
      field: "platform",
      align: "center",
      headerName: "Manufacturer",
      description: "Device manufacturer or platform (e.g. Tekktelic, Oyster)",
      headerAlign: "center",
      id: "platform",
      width: 130,
      minWidth: 130,
      renderCell: (data) => {
        if (data.rowNode.type === "group" || data.rowNode.type === "pinnedRow") return data.value;
        const platform = data.row?.device?.platform || t("dnpinput1");
        const pill = <ManufacturerPill label={platform} />;
        const displayPlatform = platform && String(platform).trim() !== "" && platform !== t("dnpinput1") ? platform : null;
        return displayPlatform ? (
          <Tooltip title={displayPlatform} enterDelay={400} placement="top-start">
            <span style={{ display: "inline-flex", minWidth: 0, width: "100%", justifyContent: "center" }}>{pill}</span>
          </Tooltip>
        ) : (
          pill
        );
      },
      valueGetter: (value, row) => row?.device?.platform || t("dnpinput1"),
      groupingValueGetter: (value, row) => row?.device?.platform || null,
    },
    {
      accessor: "assetType",
      align: "center",
      field: "assetType",
      headerName: "Tracker Type",
      description: "Hardware type: Tag, Tekktelic, NFC, Oyster, etc.",
      headerAlign: "center",
      id: "assetType",
      width: 125,
      minWidth: 125,
      renderCell: (data) => {
        if (data.rowNode.type === "group" || data.rowNode.type === "pinnedRow") return data.value;
        const type = data.row?.assetType;
        return <TrackerTypePill type={type} />;
      },
      valueGetter: (value, row) => row?.assetType,
      groupingValueGetter: (value, row) => row?.assetType || null,
    },
    {
      availableAggregationFunctions: ["avg", "min", "max"],
      align: "center",
      field: "batteryLife",
      headerName: "Battery Life (%)",
      description: "Remaining battery percentage; N/A for NFC (passive) tags",
      headerAlign: "center",
      id: "batteryLife",
      width: 130,
      minWidth: 130,
      renderCell: (data) => {
        const { row } = data;
        if (data.rowNode.type === "group" || data.rowNode.type === "pinnedRow") return data.value;

        const isNfc = row?.assetType === "NFC";
        const percent = isNfc
          ? null
          : row?.device?.batteryPercent != null
            ? row.device.batteryPercent / 100
            : row?.propertiesMap?.battery != null
              ? row.propertiesMap.battery / 100
              : null;
        const pill = <BatteryLifePill percent={percent} isNfc={isNfc} />;
        const title = isNfc ? "N/A (NFC tags have no battery)" : percent != null ? `${percent}%` : null;
        return title ? (
          <Tooltip title={title} enterDelay={400} placement="top-start">
            <span style={{ display: "inline-flex", minWidth: 0, width: "100%", justifyContent: "center" }}>{pill}</span>
          </Tooltip>
        ) : (
          pill
        );
      },
      type: "number",
      valueGetter: (value, row) => {
        // NFC tags don't have batteries
        if (row?.assetType === "NFC") return null;
        return row?.device?.batteryPercent / 100 || row?.propertiesMap?.battery / 100 || null;
      },
      groupingValueGetter: (value, row) => {
        if (row?.assetType === "NFC") return null;
        return row.device?.batteryPercent ? row.device?.batteryPercent / 100 : row.propertiesMap?.battery ? row.propertiesMap.battery / 100 : null
      }
    },
  ];
}
